/**
 * HexAlertPopover — hover card for a hex. Shows the camera's full breakdown that the hex
 * itself only hints at: LIVE / RECORDING status and the per-severity alert counts, plus
 * the latest alert (name/time/snapshot) when one is active. Screen-space, non-interactive.
 *
 * Styled as a diagram CALLOUT (see mock ss4.png): a thin leader line runs from a dot on
 * the hex's edge, up a 45° diagonal to an elbow, then along a horizontal rule that the
 * card sits on — all tinted with the alert's severity colour (neutral grey when the
 * camera has no active alert). Prefers up-and-right of the hex; mirrors left / flips
 * below when it would leave the viewport.
 */
import type { HexAlert, HexCounter } from '../../api/contracts';

const CARD_W = 248;
const MARGIN = 8;
/** Length of the 45° diagonal leader segment (run == rise). */
const DIAG = 26;

/** Brand severity colours — leader line, rule and title tint. */
const SEV_COLOR: Record<string, string> = {
  critical: '#e61b24',
  warning: '#f59e0b',
  normal: '#015ab5',
};
/** Callout tint when the camera has no active alert. */
const NEUTRAL_COLOR = '#64748b';

const SEV_CHIP: Record<string, string> = {
  critical: 'bg-red-500 text-white',
  warning: 'bg-amber-400 text-amber-950',
  normal: 'bg-blue-500 text-white',
};

const SEV_COUNTS: { key: keyof HexCounter; label: string; dot: string }[] = [
  { key: 'critical', label: 'Critical', dot: 'bg-red-500' },
  { key: 'warning', label: 'Warning', dot: 'bg-amber-400' },
  { key: 'normal', label: 'Info', dot: 'bg-blue-500' },
];

function fmtTime(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** Compact inline status: dot + label, green when running, grey when off. */
function StatusChip({ label, on }: { label: string; on: boolean }) {
  return (
    <span className={`flex items-center gap-1 text-[10px] font-medium ${on ? 'text-emerald-500' : 'text-muted-foreground'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-emerald-400' : 'bg-slate-400/60'}`} />
      {label}
    </span>
  );
}

interface HexAlertPopoverProps {
  /** Latest active alert for this camera, if any. */
  alert?: HexAlert;
  name: string;
  live: boolean;
  recording: boolean;
  counter?: HexCounter;
  anchorX: number;
  anchorY: number;
  hexHalfW: number;
  viewportW: number;
  viewportH: number;
  /**
   * Extra label/value rows rendered above the alert counts. Map View passes the camera's
   * surveyed install detail (height, facing, tilt, field of view, placement note) so an
   * operator can read WHERE and HOW a camera is mounted without leaving the plan. The
   * hexagon board has no such data and omits it.
   */
  details?: { label: string; value: string }[];
  /** Free-text note shown under `details`. */
  note?: string | null;
}

export default function HexAlertPopover({
  alert,
  name,
  live,
  recording,
  counter,
  anchorX,
  anchorY,
  hexHalfW,
  viewportW,
  viewportH,
  details,
  note,
}: HexAlertPopoverProps) {
  const img = alert?.snapshotUrl || null;
  // Flat-top hex aspect: HEX_H / HEX_W = 128 / 144.
  const hexHalfH = hexHalfW * (128 / 144);
  // Rough height only to decide whether there's room above; the card is anchored to the
  // rule by a translate so exact height doesn't matter.
  const detailRows = details?.length ?? 0;
  const estH = (img ? 225 : 155) + detailRows * 15 + (note ? 26 : 0);

  const color = alert ? SEV_COLOR[alert.severity] || SEV_COLOR.normal : NEUTRAL_COLOR;

  // Callout direction: up-right by default (ss4), mirrored/flipped to stay in view.
  const fitsRight = anchorX + hexHalfW * 0.55 + DIAG + CARD_W <= viewportW - MARGIN;
  const dirX = fitsRight ? 1 : -1;
  const roomAbove = anchorY - hexHalfH * 0.55 - DIAG - estH >= MARGIN;
  const roomBelow = anchorY + hexHalfH * 0.55 + DIAG + estH <= viewportH - MARGIN;
  const placeBelow = !roomAbove && roomBelow;
  const dirY = placeBelow ? 1 : -1;

  // Leader: dot on the hex's edge → 45° diagonal → elbow, then a horizontal rule the
  // card sits on (the rule doubles as the card's coloured underline).
  const startX = anchorX + dirX * hexHalfW * 0.55;
  const startY = anchorY + dirY * hexHalfH * 0.55;
  const elbowX = startX + dirX * DIAG;
  const elbowY = startY + dirY * DIAG;
  const ruleEndX = elbowX + dirX * CARD_W;

  const cardLeft = dirX > 0 ? elbowX : elbowX - CARD_W;
  const cardTop = placeBelow ? elbowY + 4 : elbowY - 4;
  const cardTransform = placeBelow ? undefined : 'translateY(-100%)';

  const total = counter?.total ?? 0;

  return (
    <>
      <svg className="pointer-events-none absolute left-0 top-0 z-30 h-full w-full overflow-visible">
        <circle cx={startX} cy={startY} r={2.5} fill={color} />
        <line x1={startX} y1={startY} x2={elbowX} y2={elbowY} stroke={color} strokeWidth={1.5} />
        <line x1={elbowX} y1={elbowY} x2={ruleEndX} y2={elbowY} stroke={color} strokeWidth={1.5} />
      </svg>

      <div
        className="pointer-events-none absolute z-30"
        style={{ left: cardLeft, top: cardTop, width: CARD_W, transform: cardTransform }}
      >
        <div className="relative overflow-hidden rounded-md border border-border bg-popover shadow-modal">
          {/* Uppercase callout title in the severity tint (ss4-style label). */}
          <div className="flex items-center justify-between gap-2 px-2.5 pt-2">
            <span
              className="truncate text-[11px] font-bold uppercase tracking-wider"
              style={{ color }}
            >
              {name}
            </span>
            {alert && (
              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${SEV_CHIP[alert.severity] || SEV_CHIP.normal}`}>
                {alert.severity}
              </span>
            )}
          </div>

          {img && <img src={img} alt="snapshot" className="mt-2 h-28 w-full object-cover" loading="lazy" />}

          <div className="space-y-2 p-2.5">
            {/* Live / Recording status — single compact row */}
            <div className="flex items-center gap-3">
              <StatusChip label="Live" on={live} />
              <StatusChip label="Recording" on={recording} />
            </div>

            {/* Surveyed install detail (Map View only) — where and how this camera sits. */}
            {(detailRows > 0 || note) && (
              <div className="space-y-0.5 border-t border-border pt-1.5">
                {details?.map((d) => (
                  <div key={d.label} className="flex items-baseline justify-between gap-2">
                    <span className="text-[10px] text-muted-foreground">{d.label}</span>
                    <span className="truncate text-[10px] font-medium tabular-nums text-popover-foreground">
                      {d.value}
                    </span>
                  </div>
                ))}
                {note && <p className="line-clamp-2 pt-0.5 text-[10px] italic text-muted-foreground">{note}</p>}
              </div>
            )}

            {/* Per-severity alert counts */}
            <div className="border-t border-border pt-1.5">
              {total > 0 ? (
                <div className="flex items-center justify-between gap-1">
                  {SEV_COUNTS.map((sv) => (
                    <span key={sv.key} className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className={`h-1.5 w-1.5 rounded-full ${sv.dot}`} />
                      {sv.label} <span className="font-semibold tabular-nums text-popover-foreground">{counter?.[sv.key] ?? 0}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-[10px] text-muted-foreground">No active alerts</span>
              )}
            </div>

            {/* Latest alert detail */}
            {alert && (
              <div className="space-y-1 border-t border-border pt-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[11px] font-medium text-popover-foreground">
                    {alert.alertName || alert.eventType || 'Alert'}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">{fmtTime(alert.timestamp)}</span>
                </div>
                {alert.description && <p className="line-clamp-2 text-[10px] text-muted-foreground">{alert.description}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
